/*
 * Pipeline Theme — custom JS
 *
 * Mobile-only PLP image swipe carousel.
 *
 * Defines a <pgi-mobile-carousel> custom element that lives inside a
 * <product-grid-item-variant> card. It:
 *   - listens for horizontal swipes on the card's image stack
 *   - advances / rewinds the existing `data-grid-current-image` image
 *     (infinite loop via modulo)
 *   - renders its own floating dot pagination, 8px from the bottom
 *
 * Doesn't touch desktop: the element is only attached when the card is in
 * mobile mode (the theme already toggles `.is-mobile` on resize).
 */

(function () {
  if (window.__pgiMobileCarouselInstalled) return;
  window.__pgiMobileCarouselInstalled = true;

  var MOBILE_BREAKPOINT = 768; // matches theme's medium breakpoint
  var SWIPE_THRESHOLD = 30; // px before we count a horizontal swipe
  var DIRECTIONAL_LOCK = 8; // px deltaX before we say "this is horizontal"

  function isMobileViewport() {
    var w = window.innerWidth || document.documentElement.clientWidth || 0;
    return w < MOBILE_BREAKPOINT;
  }

  class PGIMobileCarousel extends HTMLElement {
    constructor() {
      super();
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onDotClick = this._onDotClick.bind(this);
    }

    connectedCallback() {
      this.variant = this.closest('product-grid-item-variant');
      this.slide = this.variant && this.variant.querySelector('[data-grid-slide]');
      if (!this.variant || !this.slide) return;

      this.images = this.variant.querySelectorAll('[data-grid-image]');
      if (this.images.length < 2) return;

      this._renderDots();
      this._syncDotsToCurrent();

      this.slide.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.slide.addEventListener('touchmove', this._onTouchMove, { passive: false });
      this.slide.addEventListener('touchend', this._onTouchEnd, { passive: true });
      this.slide.addEventListener('touchcancel', this._onTouchEnd, { passive: true });

      // Keep dot pagination in sync if something else (filter, swatch click)
      // moves the current image.
      this._observer = new MutationObserver(() => this._syncDotsToCurrent());
      this.images.forEach((img) => {
        this._observer.observe(img, { attributes: true, attributeFilter: ['data-grid-current-image'] });
      });
    }

    disconnectedCallback() {
      if (this.slide) {
        this.slide.removeEventListener('touchstart', this._onTouchStart);
        this.slide.removeEventListener('touchmove', this._onTouchMove);
        this.slide.removeEventListener('touchend', this._onTouchEnd);
        this.slide.removeEventListener('touchcancel', this._onTouchEnd);
      }
      if (this._observer) this._observer.disconnect();
    }

    _renderDots() {
      var dots = '';
      for (var i = 0; i < this.images.length; i++) {
        dots += '<button type="button" class="pgi-mobile-carousel__dot" data-pgi-dot="' + i + '" aria-label="Go to image ' + (i + 1) + '"></button>';
      }
      this.innerHTML = dots;
      this.addEventListener('click', this._onDotClick);
    }

    _onDotClick(e) {
      var dot = e.target.closest('[data-pgi-dot]');
      if (!dot) return;
      e.preventDefault();
      e.stopPropagation();
      var index = parseInt(dot.getAttribute('data-pgi-dot'), 10);
      if (Number.isNaN(index)) return;
      this._goTo(index);
    }

    _currentIndex() {
      var current = this.variant.querySelector('[data-grid-image][data-grid-current-image]');
      if (!current) return 0;
      var idx = parseInt(current.getAttribute('data-grid-image'), 10);
      return Number.isNaN(idx) ? 0 : idx;
    }

    _goTo(index) {
      var count = this.images.length;
      if (!count) return;
      var next = ((index % count) + count) % count;
      var current = this._currentIndex();
      if (next === current) return;

      this.images.forEach((img) => img.removeAttribute('data-grid-current-image'));
      var target = this.variant.querySelector('[data-grid-image="' + next + '"]');
      if (target) {
        target.setAttribute('data-grid-current-image', 'true');
        // Make sure the image is actually loaded — the original code lazy-loads
        // everything past index 0 via `loading="lazy"`. Re-set to eager so the
        // browser kicks off the request immediately on swipe.
        target.setAttribute('loading', 'eager');
      }
      this._syncDotsToCurrent(next);
    }

    _syncDotsToCurrent(forcedIndex) {
      var idx = typeof forcedIndex === 'number' ? forcedIndex : this._currentIndex();
      this.querySelectorAll('[data-pgi-dot]').forEach((dot) => {
        var i = parseInt(dot.getAttribute('data-pgi-dot'), 10);
        dot.classList.toggle('is-active', i === idx);
      });
    }

    _onTouchStart(e) {
      if (!e.touches || e.touches.length !== 1) {
        this._touch = null;
        return;
      }
      var t = e.touches[0];
      this._touch = {
        startX: t.clientX,
        startY: t.clientY,
        lastX: t.clientX,
        locked: null, // 'h' or 'v' once we decide
      };
    }

    _onTouchMove(e) {
      if (!this._touch || !e.touches || e.touches.length !== 1) return;
      var t = e.touches[0];
      var dx = t.clientX - this._touch.startX;
      var dy = t.clientY - this._touch.startY;
      this._touch.lastX = t.clientX;

      if (!this._touch.locked) {
        if (Math.abs(dx) > DIRECTIONAL_LOCK || Math.abs(dy) > DIRECTIONAL_LOCK) {
          this._touch.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        }
      }

      if (this._touch.locked === 'h') {
        // Block the page from scrolling sideways and stop the link's tap from
        // navigating mid-swipe.
        if (e.cancelable) e.preventDefault();
      }
    }

    _onTouchEnd() {
      if (!this._touch) return;
      var dx = this._touch.lastX - this._touch.startX;
      var locked = this._touch.locked;
      this._touch = null;

      if (locked !== 'h') return;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;

      var current = this._currentIndex();
      if (dx < 0) {
        this._goTo(current + 1); // swipe left → next
      } else {
        this._goTo(current - 1); // swipe right → prev
      }
    }
  }

  if (!customElements.get('pgi-mobile-carousel')) {
    customElements.define('pgi-mobile-carousel', PGIMobileCarousel);
  }

  function attachToCard(variantEl) {
    if (!variantEl || variantEl.__pgiCarouselAttached) return;
    if (!variantEl.classList.contains('is-mobile')) return;
    var slide = variantEl.querySelector('[data-grid-slide]');
    if (!slide) return;
    var images = variantEl.querySelectorAll('[data-grid-image]');
    if (images.length < 2) return;

    variantEl.__pgiCarouselAttached = true;
    var el = document.createElement('pgi-mobile-carousel');
    // Place it as a sibling of the image stack so it overlays via absolute
    // positioning from CSS, not inside <a data-grid-link> (so dot clicks
    // don't bubble as a card navigation).
    var anchor = slide.parentElement; // the <a data-grid-link>
    var container = anchor && anchor.parentElement; // .product-grid-item__container
    if (container) {
      container.appendChild(el);
    } else {
      slide.appendChild(el);
    }
  }

  function attachAll() {
    if (!isMobileViewport()) return;
    document.querySelectorAll('product-grid-item-variant.is-mobile').forEach(attachToCard);
  }

  // Initial pass — defer to let the theme's own `connectedCallback` run first
  // (it adds `.is-mobile` based on window width).
  function init() {
    attachAll();

    // The theme listens for `theme:resize:width` and toggles `.is-mobile` on
    // each card. Re-attach when that fires so cards becoming mobile pick the
    // carousel up.
    document.addEventListener('theme:resize:width', attachAll);

    // PLP filter / pagination swaps in fresh cards. Watch for new
    // product-grid-item-variant additions and attach.
    var mo = new MutationObserver((mutations) => {
      if (!isMobileViewport()) return;
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('product-grid-item-variant')) {
            attachToCard(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('product-grid-item-variant').forEach(attachToCard);
          }
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
